import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Star, ArrowRight } from 'lucide-react';
import { LOGIN_BANNERS, type LoginBanner } from './loginBanners';
import { cn } from '@/lib/utils';
import { useActiveLoginBanners } from '@/hooks/useLoginBanners';
import { getBannerTag } from './bannerTags';

const INTERVAL = 7000;

export default function LoginBannerMiniCard() {
  const { data, isError } = useActiveLoginBanners();
  const banners: LoginBanner[] = useMemo(() => {
    if (isError || !data?.length) return LOGIN_BANNERS;
    return data.map((b) => ({
      id: b.id,
      tag: b.tag ?? undefined,
      title: b.title,
      highlight: b.highlight ?? undefined,
      description: b.description,
      ctaLabel: b.cta_label ?? undefined,
      ctaHref: b.cta_href ?? undefined,
    }));
  }, [data, isError]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  useEffect(() => { setIndex(0); }, [banners.length]);

  const go = useCallback(
    (dir: number) => setIndex((i) => (i + dir + banners.length) % banners.length),
    [banners.length],
  );

  useEffect(() => {
    if (paused || banners.length < 2) return;
    const t = window.setTimeout(() => go(1), INTERVAL);
    return () => window.clearTimeout(t);
  }, [index, paused, go, banners.length]);

  if (!banners.length) return null;
  const b = banners[index] ?? banners[0];

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Novidades do AutoFluxo"
      className="mx-auto flex min-h-[132px] w-full max-w-md flex-col justify-center rounded-2xl border border-white/10 bg-black/60 px-4 py-3 shadow-xl backdrop-blur-xl"
      onTouchStart={(e) => { setPaused(true); touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        setPaused(false);
        if (start == null) return;
        const dx = e.changedTouches[0].clientX - start;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      <div key={b.id} className="animate-fade-in space-y-1">
        <div className="flex items-start gap-2">
          {b.tag && (
            <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', getBannerTag(b.tag).slider)}>
              <Star className="h-2.5 w-2.5 fill-current" />
              {b.tag}
            </span>
          )}
          <h3 className="line-clamp-2 text-sm font-bold text-white">
            {b.title}
            {b.highlight && <span className="text-imperial-red"> {b.highlight}</span>}
          </h3>
        </div>
        <p className="line-clamp-3 text-xs leading-snug text-white/75">{b.description}</p>
        <div className="flex items-center justify-between pt-1">
          {b.ctaLabel && b.ctaHref ? (
            <a href={b.ctaHref} className="inline-flex items-center gap-1 text-xs font-medium text-imperial-red">
              {b.ctaLabel} <ArrowRight className="h-3 w-3" />
            </a>
          ) : <span />}
          {banners.length > 1 && (
            <div className="flex gap-1.5">
              {banners.map((x, i) => (
                <button
                  key={x.id}
                  type="button"
                  aria-label={`Ir para novidade ${i + 1}`}
                  aria-current={i === index}
                  onClick={() => setIndex(i)}
                  className={cn('h-1.5 rounded-full transition-all', i === index ? 'w-4 bg-imperial-red' : 'w-1.5 bg-white/40')}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
